import { defineComponent, h } from 'vue';
import { useInspectionReport } from '@/ui/components/inspectionReport/useInspectioReport';
import { LegacyForm, LegacyItem, LegacyRoccResults } from '@/ui/components/inspectionReport/legacyTypes';
import { t } from '@/ui/i18n/i18n';
import TableBox from '@/ui/components/inspectionReport/items/table/TableBox.vue';

const sortResultsByCategory = (results: LegacyRoccResults, categoryId2Name: Record<string, string>): LegacyRoccResults =>
  [...results].sort((a, b) => {
    const labelA = categoryId2Name[a.category!];
    const labelB = categoryId2Name[b.category!];

    return labelA.localeCompare(labelB);
  });

export const RoccScoreTable = defineComponent({
  name: 'RoccScoreTable',
  setup() {
    const { resource, categories } = useInspectionReport();

    const renderTopic = (item: LegacyItem, rowspan: number) => {
      const title = (item.params && item.params.header) ? t('inspection-report.header') : (item.title || '-');
      return h('td', { attrs: { rowspan }, class: 'inspection-report__table-row' }, title);
    };

    const itemFilter = (item: LegacyItem): boolean =>
      (!item.params || !item.params.header || item.max_score === undefined || item.max_score > 0)
      && item.params?.rocc_enabled === true && item.rocc_results !== undefined;

    const getPagesWithRocc = (form?: LegacyForm) => (form?.items ?? []).filter(item => itemFilter(item));

    return function render() {
      const form = resource.value?.form;
      const formLevelRoccEnabled = form?.params?.rocc_enabled;
      const roccItems = formLevelRoccEnabled ? [form!] : getPagesWithRocc(form);
      const categoryId2Name = Object.fromEntries(
        categories.value.map(({ value, label }) => [value, label]),
      );

      return h('table', {
        class: 'mb-4 inspection-report__table',
      }, [
        h('thead', {}, [
          h('tr', {}, [
            formLevelRoccEnabled ? null : h('td', { class: ' w-1/2 inspection-report__table-header' }, [t('inspection-report.topics')]),
            h('td', { class: 'inspection-report__table-header' }, [
              h('span', [t('inspection-report.severity-levels'),
              ])]),
            h('td', { class: 'inspection-report__table-header' }, [
              h('span', [t('inspection-report.severity-amount'),
              ])]),
          ]),
        ]),
        h('tbody', {},
          roccItems
            .map(item => {
              const results = item.rocc_results ? sortResultsByCategory(item.rocc_results, categoryId2Name).map(result => [
                h(TableBox, [categoryId2Name[result.category!]]),
                h(TableBox, [
                  h('div', { class: 'flex flex-row inspection-report__response-container' }, [
                    h('div', {
                      class: 'inspection-report__response-bullet',
                      attrs: { style: `background-color:${result.passing ? '#4AD862' : '#FF6E44'}` },
                    }),
                    h('span', { class: 'inspection-report__response-title' }, [result.count]),
                  ]),
                ]),
              ]) : [[
                h(TableBox, ['-']),
                h(TableBox, ['0']),
              ]];

              return [
                formLevelRoccEnabled ? null : h('tr', { }, [
                  renderTopic(item as LegacyItem, item.params?.severities?.length ?? 1),
                  results.shift(),
                ]),
                results.length ? results.map(result => h('tr', {}, [result])) : null,
              ];
            })),
      ]);
    };
  },
});
